/**
 * Asistente IA del comité (Console). El tope mensual en USD es a la vez el
 * interruptor: 0 = apagado. Es un costo variable (se paga por pregunta), por
 * eso cada comité se enciende a mano. Recordar subir también el tope global
 * de la cuenta en la consola de Anthropic (suma de los topes + margen).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Badge, Card, Cargando } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { fetchTopeAsistente, putTopeAsistente } from "../asistente/api";

const TOPE_SUGERIDO = "10";

export function AsistenteEmpresaCard({ empresaId }: { empresaId: number }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["superadmin", "asistente", empresaId],
    queryFn: () => fetchTopeAsistente(empresaId),
  });
  const [tope, setTope] = useState("");
  useEffect(() => {
    if (q.data) setTope(String(Number(q.data.tope_usd_mes)));
  }, [q.data]);

  const mut = useMutation({
    mutationFn: (valor: string) => putTopeAsistente(empresaId, valor),
    onSuccess: (r) => {
      queryClient.setQueryData(["superadmin", "asistente", empresaId], r);
      toast.success(
        Number(r.tope_usd_mes) > 0
          ? `Asistente encendido con tope de US$${Number(r.tope_usd_mes)} al mes.`
          : "Asistente apagado para este comité.",
      );
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo guardar el tope"),
  });

  const actual = q.data ? Number(q.data.tope_usd_mes) : 0;
  const encendido = actual > 0;
  const valido = tope.trim() !== "" && Number(tope) >= 0 && Number(tope) <= 1000;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <Sparkles className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
          Asistente IA
        </h2>
        <Badge tone={encendido ? "emerald" : "slate"}>
          {encendido ? "Encendido" : "Apagado"}
        </Badge>
      </div>

      {q.isLoading ? (
        <Cargando inline />
      ) : (
        <>
          <p className="mb-3 text-sm text-neutral-600">
            Gasto de este mes:{" "}
            <strong>US${Number(q.data?.gasto_mes_usd ?? 0).toFixed(2)}</strong>
            {encendido && <> de US${actual}</>}. Al llegar al tope, el asistente se pausa hasta
            el día 1.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm text-neutral-700">
              <span className="mb-1 block text-xs text-neutral-500">Tope mensual (US$)</span>
              <input
                type="number"
                min={0}
                max={1000}
                step="1"
                value={tope}
                onChange={(e) => setTope(e.target.value)}
                className="w-28 rounded-md border border-neutral-450 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <button
              type="button"
              onClick={() => mut.mutate(tope)}
              disabled={!valido || Number(tope) === actual || mut.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Guardar
            </button>
            {!encendido && (
              <button
                type="button"
                onClick={() => mut.mutate(TOPE_SUGERIDO)}
                disabled={mut.isPending}
                className="rounded-md px-3 py-1.5 text-sm text-brand-700 hover:bg-brand-50"
              >
                Encender con US${TOPE_SUGERIDO}
              </button>
            )}
            {encendido && (
              <button
                type="button"
                onClick={() => mut.mutate("0")}
                disabled={mut.isPending}
                className="rounded-md px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50"
              >
                Apagar
              </button>
            )}
          </div>
          <p className="mt-3 text-xs text-neutral-500">
            Al subir un tope, ajusta también el límite global de la cuenta en la consola de
            Anthropic.
          </p>
        </>
      )}
    </Card>
  );
}
